import type { ProductList } from "@/app/(main)/(search)/voyages/[slug]/getProductList";
import { dayjs } from "@/lib/dayjs";
import { safeParse } from "valibot";
import {
  type ConfigValue,
  OptionTypeSchema,
  type ProductListValues,
  type SelectedValue,
  type Value,
  type ValueType,
} from "./types";

export const configTypes = ["sort", "slug", "gte", "page", "lte"];
export const fixedTypes = ["alias"];

export const getTermFromSearchParams = (searchParams: { [key: string]: string | string[] | undefined }): string => {
  if (searchParams.term && typeof searchParams.term === "string") {
    return searchParams.term;
  }

  return "";
};

const buildValues = (values: Array<Value>, type: ValueType, params: string | string[] | undefined) => {
  if (params !== undefined) {
    if (typeof params === "string") {
      values.push({
        type,
        value: params,
      });
    } else {
      for (const iterator of params) {
        values.push({
          type,
          value: iterator,
        });
      }
    }
  }
};

export const getSelectedValueFromSearchParams = (searchParams: {
  [key: string]: string | string[] | undefined;
}): SelectedValue | null => {
  if (searchParams.selectedValue && typeof searchParams.selectedValue === "string") {
    const [rawType, rawValue] = searchParams.selectedValue.split("|");
    if (rawType && rawValue) {
      const type = safeParse(OptionTypeSchema, rawType);
      if (type.success) {
        const value: SelectedValue = {
          type: type.output,
          value: rawValue,
        };
        return value;
      }
    }
  }

  return null;
};

export const getValuesFromSearchParams = (searchParams: {
  [key: string]: string | string[] | undefined;
}): Array<Value> => {
  const values: Array<Value> = [];

  if (searchParams.durations) {
    buildValues(values, "durations", searchParams.durations);
  }

  if (searchParams.themes) {
    buildValues(values, "themes", searchParams.themes);
  }

  if (searchParams.rythme) {
    buildValues(values, "rythme", searchParams.rythme);
  }

  if (searchParams.destinations) {
    buildValues(values, "destinations", searchParams.destinations);
  }

  if (searchParams.speakers) {
    buildValues(values, "speakers", searchParams.speakers);
  }

  if (searchParams.budgets) {
    buildValues(values, "budgets", searchParams.budgets);
  }

  if (searchParams.startMonths) {
    buildValues(values, "startMonths", searchParams.startMonths);
  }

  if (searchParams.garanti) {
    buildValues(values, "garanti", searchParams.garanti);
  }

  if (searchParams.isFitTemplate) {
    buildValues(values, "isFitTemplate", searchParams.isFitTemplate);
  }

  return values;
};

const ALLOWED_SORT_VALUES = ["price:asc", "price:desc", "date:asc", "date:desc"];

export const getConfigValuesFromSearchParams = (searchParams: { [key: string]: string | string[] | undefined }): [
  Array<ConfigValue>,
  {
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
  } | null,
] => {
  const configValues: Array<ConfigValue> = [];

  let dates: {
    start: dayjs.Dayjs;
    end: dayjs.Dayjs;
  } | null = null;

  if (
    searchParams.gte &&
    searchParams.lte &&
    typeof searchParams.gte === "string" &&
    typeof searchParams.lte === "string"
  ) {
    const start = dayjs.utc(searchParams.gte, "DD-MM-YYYY");
    const end = dayjs.utc(searchParams.lte, "DD-MM-YYYY");
    if (start.isValid() && end.isValid() && (start.isBefore(end) || start.isSame(end))) {
      configValues.push({
        value: start.format("YYYY-MM-DD"),
        type: "gte",
      });
      configValues.push({
        value: end.format("YYYY-MM-DD"),
        type: "lte",
      });
      dates = {
        start,
        end,
      };
    }
  }

  if (searchParams.sort && typeof searchParams.sort === "string" && ALLOWED_SORT_VALUES.includes(searchParams.sort)) {
    configValues.push({
      value: searchParams.sort,
      type: "sort",
    });
  }

  return [configValues, dates];
};

export const getPathname = (pathname: string): string => {
  const splits = pathname.split("/");
  const last = splits[splits.length - 2];

  if (last && last !== "" && !Number.isNaN(Number(last))) {
    return `${splits.slice(0, splits.length - 2).join("/")}/`;
  }

  return pathname;
};

export const getProductListValuesFromProductList = (productList: ProductList): ProductListValues => {
  return {
    travelIds: productList.travelIds,
    destinations: productList.destinations,
    gte: productList.fromDate,
    lte: productList.toDate,
  };
};
